import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Calculator, RefreshCw, Loader2, AlertTriangle, CheckCircle2, Wallet,
  Receipt, FileText, ShieldCheck, Clock, Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const FRAZIONAMENTI = [
  { value: "annuale", label: "Annuale (1 rata)" },
  { value: "semestrale", label: "Semestrale (2 rate)" },
  { value: "quadrimestrale", label: "Quadrimestrale (3 rate)" },
  { value: "trimestrale", label: "Trimestrale (4 rate)" },
  { value: "mensile", label: "Mensile (12 rate)" },
];

const eur = (n) =>
  typeof n === "number"
    ? n.toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2 })
    : "—";

export default function RisPremiumCalculator({ richiesta, onUpdated }) {
  const [ultimo, setUltimo] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const loadUltimo = useCallback(async () => {
    if (!richiesta?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ris/calcola-premio/${richiesta.id}`, { withCredentials: true });
      setUltimo(r.data.calcolo);
      setHistoryCount(r.data.history_count);
    } catch (e) {
      // 404 = nessun calcolo precedente: caso normale
      if (e?.response?.status !== 404) {
        toast.error(e?.response?.data?.detail || "Errore caricamento calcolo");
      }
      setUltimo(null);
      setHistoryCount(0);
    } finally {
      setLoading(false);
    }
  }, [richiesta?.id]);

  useEffect(() => { loadUltimo(); }, [loadUltimo]);

  // Fase 9C — Download PDF preventivo
  const [downloading, setDownloading] = useState(false);
  const handleDownloadPdf = async () => {
    if (!richiesta?.id || !ultimo) return;
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/admin/ris/preventivo-pdf/${richiesta.id}`, {
        withCredentials: true,
        responseType: "blob",
        params: ultimo.id ? { calcolo_id: ultimo.id } : {},
      });
      // Estrai filename dal Content-Disposition
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `Preventivo_RiS_${richiesta.id}.pdf`;
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Preventivo PDF scaricato");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Errore download PDF";
      toast.error(typeof detail === "string" ? detail : "Errore download PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 sm:p-5">
      {/* v1.73.1 — Sanitaria lead-only per il cliente. Banner uso interno. */}
      <div
        className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
        data-testid="ris-uso-interno-banner"
      >
        <span className="font-semibold">⚠️ Uso interno</span>
        {" — "}
        Il premio calcolato è per <strong>preventivazione manuale</strong>.
        Sanitaria è ora <strong>lead-only</strong> per il cliente: contattalo
        per proporre l'offerta personalizzata.
      </div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-heading text-base text-[#163321] flex items-center gap-2">
          <Calculator className="w-4 h-4" /> Quotazione Premio RiS
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {historyCount > 0 && (
            <span className="text-[10px] text-zinc-500 bg-zinc-100 rounded-full px-2 py-0.5">
              {historyCount} calcoli salvati
            </span>
          )}
          {ultimo && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloading}
              data-testid="ris-pdf-download-btn"
            >
              {downloading
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Download className="w-3.5 h-3.5 mr-1" />}
              {downloading ? "Scarico…" : "Scarica preventivo PDF"}
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => setModalOpen(true)}
            className={ultimo ? "" : "bg-[#163321] hover:bg-[#163321]/90 text-white"}
            variant={ultimo ? "outline" : "default"}
            data-testid="ris-premium-calc-btn"
          >
            {ultimo ? <RefreshCw className="w-3.5 h-3.5 mr-1" /> : <Calculator className="w-3.5 h-3.5 mr-1" />}
            {ultimo ? "Ricalcola" : "Calcola premio"}
          </Button>
        </div>
      </div>

      {/* Stima validata badge — Task 2026-05-08 v3: 13/13 voci validate (100%) → VERDE pieno */}
      <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-emerald-900 leading-snug">
          ✓ <strong>Tariffa RiS completamente validata</strong> da fonte ufficiale Reale Mutua Ed. 2026.01.
          Imposta 2.5% normata (L. 1216/61) + 12 voci confermate dall'agente Colucci
          (frazionamenti, sconti polifamiliari/fedeltà/wellness, franchigia ricovero, premio minimo
          25 €/mese ↔ 300 €/anno).
        </p>
      </div>

      {/* Task D 2026-05-09: Pannello stato Extra Ricovero (production_ready toggle) */}
      <ExtractionStatusPanel />

      {loading ? (
        <div className="flex items-center justify-center py-8 text-zinc-400 text-sm">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Caricamento ultimo calcolo...
        </div>
      ) : !ultimo ? (
        <div className="text-center py-6 text-sm text-zinc-500">
          Nessuna quotazione ancora calcolata per questa richiesta.<br />
          Clicca <strong>"Calcola premio"</strong> per generare la stima.
        </div>
      ) : (
        <Breakdown ultimo={ultimo} />
      )}

      {modalOpen && (
        <CalcModal
          richiesta={richiesta}
          onClose={() => setModalOpen(false)}
          onSuccess={(calcolo) => {
            setUltimo(calcolo);
            setHistoryCount((c) => c + 1);
            setModalOpen(false);
            if (onUpdated) onUpdated();
          }}
        />
      )}
    </div>
  );
}


function Breakdown({ ultimo }) {
  const r = ultimo.risultato_completo || {};
  const norm = ultimo.engine_input_normalizzato || r.input_normalizzato || {};
  const sconti = r.sconti_applicati || [];
  const breakdown = r.breakdown || [];
  const calcolatoIl = ultimo.calcolato_il
    ? new Date(ultimo.calcolato_il).toLocaleString("it-IT")
    : "—";

  return (
    <div className="space-y-3">
      {/* Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Card icon={Wallet} color="emerald" label="Premio netto" value={eur(r.premio_netto)} />
        <Card icon={Receipt} color="zinc" label={`Imposte (${((r.imposte / r.premio_netto) * 100 || 2.5).toFixed(1)}%)`} value={eur(r.imposte)} />
        <Card icon={ShieldCheck} color="blue" label="Premio lordo annuo" value={eur(r.premio_lordo_annuo)} bold />
        <Card icon={Clock} color="purple" label={`Rata ${(norm.frazionamento || "annuale")} (×${r.rate_annue})`} value={eur(r.premio_per_frazionamento)} bold />
      </div>

      {/* Riepilogo input */}
      <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-xs grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        <KV label="Età" value={norm.eta} />
        <KV label="Variante" value={norm.variante?.replace(/_/g, " ")} />
        <KV label="Opzione" value={(norm.opzione || "").charAt(0).toUpperCase() + (norm.opzione || "").slice(1)} />
        <KV label="Zona ricovero" value={norm.zona_ricovero} />
        {norm.zona_extraricovero && <KV label="Zona extraricovero" value={norm.zona_extraricovero} />}
        <KV label="N° assicurati" value={norm.num_assicurati} />
        <KV label="Frazionamento" value={norm.frazionamento} />
        <KV label="Edizione tariffa" value={r.edizione_tariffa} />
      </div>

      {/* Provisional flag — Task D 2026-05-09 */}
      {norm.provisional === true && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-[11px] text-amber-900" data-testid="ris-provisional-banner">
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          <span>Premio Extra Ricovero basato su <strong>campione validato</strong> — tariffa completa in caricamento (modalità safe).</span>
        </div>
      )}

      {/* Sconti */}
      {sconti.length > 0 && (
        <div>
          <div className="text-xs font-medium text-zinc-700 mb-1">Sconti applicati</div>
          <div className="space-y-1">
            {sconti.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-emerald-50/50 border border-emerald-100 rounded px-3 py-1.5">
                <span><strong>{s.tipo}</strong>{s.dettaglio ? ` — ${s.dettaglio}` : ""}</span>
                <span className="text-emerald-700 font-medium">−{(s.percentuale * 100).toFixed(1)}% ({eur(s.importo)})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Breakdown */}
      <div>
        <div className="text-xs font-medium text-zinc-700 mb-1">Breakdown voci</div>
        <table className="w-full text-xs">
          <tbody>
            {breakdown.map((b, i) => (
              <tr key={i} className="border-b border-zinc-100">
                <td className="py-1.5 text-zinc-700">{b.voce}</td>
                <td className={`py-1.5 text-right font-mono ${b.importo < 0 ? "text-emerald-600" : "text-zinc-900"}`}>
                  {eur(b.importo)}
                </td>
              </tr>
            ))}
            <tr className="bg-[#163321]/5">
              <td className="py-2 font-semibold text-[#163321]">TOTALE LORDO ANNUO</td>
              <td className="py-2 text-right font-mono font-bold text-[#163321]">{eur(r.premio_lordo_annuo)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Avvisi */}
      {r.avvisi && r.avvisi.length > 0 && (
        <div className="text-[10px] text-zinc-500 space-y-1 pt-2 border-t border-zinc-100">
          {r.avvisi.map((a, i) => (
            <div key={i}>• {a}</div>
          ))}
        </div>
      )}

      {/* Footer audit */}
      <div className="flex items-center justify-between text-[10px] text-zinc-500 pt-1">
        <span>Calcolato il {calcolatoIl}{ultimo.calcolato_da_email ? ` da ${ultimo.calcolato_da_email}` : ""}</span>
        <span>audit: {(ultimo.audit_id || "").slice(0, 8)}</span>
      </div>
      {ultimo.motivo_accesso && (
        <div className="text-[10px] text-zinc-500 italic">Motivo: {ultimo.motivo_accesso}</div>
      )}
    </div>
  );
}


function Card({ icon: Icon, color, label, value, bold }) {
  const colorMap = {
    emerald: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
    zinc: "border-zinc-200 bg-zinc-50/60 text-zinc-700",
    blue: "border-blue-200 bg-blue-50/60 text-blue-700",
    purple: "border-purple-200 bg-purple-50/60 text-purple-700",
  };
  return (
    <div className={`border rounded-lg p-2.5 ${colorMap[color]}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide opacity-80">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`mt-1 text-lg ${bold ? "font-heading font-medium" : ""} text-zinc-900`}>{value}</div>
    </div>
  );
}


function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-zinc-500">{label}</div>
      <div className="text-zinc-800 font-medium">{value || "—"}</div>
    </div>
  );
}


function CalcModal({ richiesta, onClose, onSuccess }) {
  const [motivo, setMotivo] = useState("");
  const [frazionamento, setFrazionamento] = useState("annuale");
  const [busy, setBusy] = useState(false);
  // 2026-05-09 — Area Extra Ricovero (override admin)
  const [opzioneExtra, setOpzioneExtra] = useState("");  // "" = auto da richiesta
  const [reteExtra, setReteExtra] = useState("network");
  const [franchigiaExtra, setFranchigiaExtra] = useState("");
  // 2026-05-11 — Franchigia RSM (Fase 11A)
  const [franchigiaRsm, setFranchigiaRsm] = useState("no_franchigia");
  // Errore strutturato PREMIUM_CELL_NOT_LOADED (mostrato inline nel modal)
  const [premiumNotLoaded, setPremiumNotLoaded] = useState(null);
  // Errore strutturato FRANCHIGIA_MOLTIPLICATORE_NOT_LOADED (Fase 11A)
  const [franchigiaRsmNotLoaded, setFranchigiaRsmNotLoaded] = useState(null);

  const FRANCHIGIE_NETWORK = [1000, 1500, 2000, 2500, 3000, 3500, 5000];
  const FRANCHIGIE_EXTRA = [1500, 2000, 2500, 3000, 3500, 4000, 5500];
  const valoriFranchigia = reteExtra === "extranetwork" ? FRANCHIGIE_EXTRA : FRANCHIGIE_NETWORK;

  const submit = async (e) => {
    e.preventDefault();
    if (!motivo || motivo.trim().length < 10) {
      toast.error("Inserisci un motivo di almeno 10 caratteri (audit log)");
      return;
    }
    setBusy(true);
    setPremiumNotLoaded(null);
    setFranchigiaRsmNotLoaded(null);
    try {
      const body = { motivo_accesso: motivo.trim(), frazionamento };
      if (opzioneExtra) {
        body.opzione_extraricovero = opzioneExtra;
        body.rete = reteExtra;
        if (franchigiaExtra) body.franchigia_extra_eur = parseInt(franchigiaExtra, 10);
      }
      // Fase 11A — Franchigia RSM
      if (franchigiaRsm && franchigiaRsm !== "no_franchigia") {
        body.franchigia_rsm_id = franchigiaRsm;
      } else {
        body.franchigia_rsm_id = "no_franchigia";
      }
      const r = await axios.post(
        `${API}/admin/ris/calcola-premio/${richiesta.id}`,
        body,
        { withCredentials: true }
      );
      toast.success(`Premio calcolato: ${eur(r.data.calcolo.premio_lordo_annuo)} annuo`);
      onSuccess(r.data.calcolo);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      // PREMIUM_CELL_NOT_LOADED → mostro card inline (NON toast)
      if (status === 422 && detail && typeof detail === "object" && detail.error_code === "PREMIUM_CELL_NOT_LOADED") {
        setPremiumNotLoaded(detail);
      } else if (status === 422 && detail && typeof detail === "object" && detail.error_code === "FRANCHIGIA_MOLTIPLICATORE_NOT_LOADED") {
        setFranchigiaRsmNotLoaded(detail);
      } else if (status === 422) {
        toast.error(`Eligibilità: ${typeof detail === "string" ? detail : (detail?.message || "vedi dettagli")}`);
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Errore calcolo premio"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-xl max-w-md w-full p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h3 className="font-heading text-lg text-[#163321]">Calcolo premio RiS</h3>
          <p className="text-[11px] text-zinc-500 mt-1">
            Cliente: <strong>{richiesta.cliente?.nome} {richiesta.cliente?.cognome}</strong> ({richiesta.cliente?.citta}).
            Verra' scritto un audit log GDPR-compliant.
          </p>
        </div>

        <div>
          <Label className="text-xs">Frazionamento</Label>
          <Select value={frazionamento} onValueChange={setFrazionamento}>
            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FRAZIONAMENTI.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* === Area Extra Ricovero (override admin) === */}
        <div className="border border-zinc-200 rounded-lg p-3 space-y-2 bg-zinc-50/40">
          <div className="text-xs font-medium text-zinc-700">Area Extra Ricovero (override admin)</div>
          <div>
            <Label className="text-[10px] text-zinc-500">Opzione</Label>
            <Select value={opzioneExtra || "auto"} onValueChange={(v) => setOpzioneExtra(v === "auto" ? "" : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="ris-admin-opzione-extra">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto (da richiesta cliente)</SelectItem>
                <SelectItem value="alta_diagnostica">Alta Diagnostica</SelectItem>
                <SelectItem value="diagnostica_completa">Diagnostica Completa</SelectItem>
                <SelectItem value="visite_specialistiche">Visite Specialistiche</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {opzioneExtra && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-zinc-500">Rete</Label>
                  <Select value={reteExtra} onValueChange={(v) => { setReteExtra(v); setFranchigiaExtra(""); }}>
                    <SelectTrigger className="h-8 text-xs" data-testid="ris-admin-rete">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="network">Network</SelectItem>
                      <SelectItem value="extranetwork">Extra-network</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-zinc-500">Franchigia (€/anno)</Label>
                  <select
                    value={franchigiaExtra}
                    onChange={(e) => setFranchigiaExtra(e.target.value)}
                    className="h-8 w-full rounded border border-zinc-200 px-2 text-xs bg-white"
                    data-testid="ris-admin-franchigia"
                  >
                    <option value="">Nessuna</option>
                    {valoriFranchigia.map((v) => <option key={v} value={v}>{v.toLocaleString("it-IT")} €</option>)}
                  </select>
                </div>
              </div>
              <p className="text-[10px] text-amber-700">
                <AlertTriangle className="inline w-3 h-3 mr-0.5" />
                In safe-mode: solo i campioni validati hanno premio caricato. Le altre combinazioni
                restituiranno <code>PREMIUM_CELL_NOT_LOADED</code>.
              </p>
            </>
          )}
        </div>

        {/* === Franchigia RSM (Fase 11A — 2026-05-11) === */}
        <div className="border border-zinc-200 rounded-lg p-3 space-y-2 bg-zinc-50/40">
          <div className="text-xs font-medium text-zinc-700">Franchigia RSM (Rimborso Spese Mediche)</div>
          <Select value={franchigiaRsm} onValueChange={setFranchigiaRsm}>
            <SelectTrigger className="h-8 text-xs" data-testid="ris-admin-franchigia-rsm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="no_franchigia">NO FRANCHIGIA</SelectItem>
              <SelectItem value="B">Franchigia B - Network: 1000 / Fuori Network: 1500</SelectItem>
              <SelectItem value="C">Franchigia C - Network: 1500 / Fuori Network: 2000</SelectItem>
              <SelectItem value="D">Franchigia D - Network: 2000 / Fuori Network: 2500</SelectItem>
              <SelectItem value="E">Franchigia E - Network: 2500 / Fuori Network: 3000</SelectItem>
              <SelectItem value="F">Franchigia F - Network: 3000 / Fuori Network: 3500</SelectItem>
              <SelectItem value="G">Franchigia G - Network: 3500 / Fuori Network: 4000</SelectItem>
              <SelectItem value="H">Franchigia H - Network: 5000 / Fuori Network: 5500</SelectItem>
            </SelectContent>
          </Select>
          {franchigiaRsm !== "no_franchigia" && (
            <p className="text-[10px] text-amber-700">
              <AlertTriangle className="inline w-3 h-3 mr-0.5" />
              Moltiplicatori premio in safe-mode: la cella restituisce <code>FRANCHIGIA_MOLTIPLICATORE_NOT_LOADED</code> finché non importi via CSV.
            </p>
          )}
        </div>

        <div>
          <Label className="text-xs">
            Motivo accesso (audit log) <span className="text-rose-600">*</span>
          </Label>
          <Textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            placeholder="Es: 'Calcolo preventivo indicativo su richiesta cliente per follow-up commerciale'"
            className="text-sm"
            data-testid="ris-premium-motivo"
          />
          <p className="text-[10px] text-zinc-400 mt-1">Min 10 caratteri. Sara' tracciato per accountability ex art. 5 GDPR.</p>
        </div>

        {/* === PREMIUM_CELL_NOT_LOADED card === */}
        {premiumNotLoaded && (
          <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-3 space-y-1.5" data-testid="ris-premium-not-loaded-card">
            <div className="flex items-center gap-2 text-amber-900 font-medium text-xs">
              <AlertTriangle className="w-4 h-4" />
              Premio non disponibile in tariffa (PREMIUM_CELL_NOT_LOADED)
            </div>
            <p className="text-[11px] text-amber-900 leading-snug">
              {premiumNotLoaded.message}
            </p>
            {premiumNotLoaded.details && (
              <pre className="text-[10px] bg-white/60 p-2 rounded border border-amber-200 overflow-x-auto">
                {JSON.stringify({
                  opzione: premiumNotLoaded.details.opzione_extraricovero,
                  livello: premiumNotLoaded.details.livello,
                  zona: premiumNotLoaded.details.zona,
                  eta: premiumNotLoaded.details.eta,
                  variante: premiumNotLoaded.details.variante,
                }, null, 2)}
              </pre>
            )}
            <p className="text-[10px] text-amber-700 italic">{premiumNotLoaded.suggestion}</p>
          </div>
        )}

        {/* === FRANCHIGIA_MOLTIPLICATORE_NOT_LOADED card (Fase 11A) === */}
        {franchigiaRsmNotLoaded && (
          <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-3 space-y-1.5" data-testid="ris-franchigia-rsm-not-loaded-card">
            <div className="flex items-center gap-2 text-amber-900 font-medium text-xs">
              <AlertTriangle className="w-4 h-4" />
              Moltiplicatore franchigia non caricato
            </div>
            <p className="text-[11px] text-amber-900 leading-snug">{franchigiaRsmNotLoaded.message}</p>
            {franchigiaRsmNotLoaded.details && (
              <pre className="text-[10px] bg-white/60 p-2 rounded border border-amber-200 overflow-x-auto">
                {JSON.stringify({
                  franchigia: franchigiaRsmNotLoaded.details.franchigia_id,
                  livello: franchigiaRsmNotLoaded.details.livello,
                  network_eur: franchigiaRsmNotLoaded.details.network_eur,
                  fuori_network_eur: franchigiaRsmNotLoaded.details.fuori_network_eur,
                }, null, 2)}
              </pre>
            )}
            <p className="text-[10px] text-amber-700 italic">{franchigiaRsmNotLoaded.suggestion}</p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>Annulla</Button>
          <Button type="submit" size="sm" disabled={busy} className="bg-[#163321] hover:bg-[#163321]/90 text-white" data-testid="ris-premium-confirm">
            {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Calcolo...</> : <><Calculator className="w-3.5 h-3.5 mr-1" /> Calcola</>}
          </Button>
        </div>
      </form>
    </div>
  );
}



// ============================================================================
// Task D 2026-05-09 — Pannello stato production_ready Extra Ricovero
// ============================================================================
function ExtractionStatusPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null); // chiave opzione in toggle

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/ris/extraction/status`, { withCredentials: true });
      setStatus(r.data);
    } catch (e) {
      // 401/403 silenzioso (pannello opzionale)
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (opzione, currentValue) => {
    const next = !currentValue;
    if (next && !window.confirm(
      `Confermi l'attivazione di production_ready per "${opzione}"?\n\n` +
      `Una volta attivo, i preventivi RiS non saranno più marcati come "provvisori".`
    )) return;
    setBusy(opzione);
    try {
      await axios.post(
        `${API}/admin/ris/extraction/production-ready`,
        { opzione, value: next },
        { withCredentials: true }
      );
      toast.success(`${opzione}: ${next ? "PRODUCTION" : "SAFE"}`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422 && detail?.error_code === "EXTRACTION_INCOMPLETE") {
        toast.error(`Estrazione incompleta: ${detail.completed_cells}/${detail.total_expected_cells} celle (mancano ${detail.missing_cells})`);
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Errore toggle"));
      }
    } finally {
      setBusy(null);
    }
  };

  if (!status) return null;
  return (
    <div className="border border-zinc-200 rounded-lg p-2.5 mb-3 bg-zinc-50/40" data-testid="ris-extraction-status-panel">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1.5">Stato tariffa Extra Ricovero</div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(status).map(([opz, s]) => {
          const ready = s.production_ready;
          const pct = s.total_expected_cells > 0
            ? Math.round((s.completed_cells / s.total_expected_cells) * 100)
            : 0;
          return (
            <button
              key={opz}
              onClick={() => toggle(opz, ready)}
              disabled={busy === opz}
              className={`text-[10px] px-2 py-1 rounded border ${
                ready
                  ? "bg-emerald-100 border-emerald-300 text-emerald-900"
                  : "bg-amber-100 border-amber-300 text-amber-900"
              } hover:opacity-80 transition-opacity disabled:opacity-50`}
              data-testid={`ris-toggle-${opz}`}
              title={`${s.completed_cells}/${s.total_expected_cells} celle caricate (${pct}%)`}
            >
              {busy === opz ? "..." : (
                <>
                  {ready ? "● PROD" : "○ SAFE"} {opz} <span className="opacity-60">({pct}%)</span>
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Task D-tris 2026-05-09: Import bulk CSV */}
      <BulkImportSection onImported={load} />
    </div>
  );
}



// ============================================================================
// Task D-tris 2026-05-09 — Sezione bulk import CSV
// ============================================================================
function BulkImportSection({ onImported }) {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [overwrite, setOverwrite] = useState(false);
  const [modulo, setModulo] = useState("extra_ricovero");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/admin/ris/extraction/csv-template?modulo=${modulo}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ris_template_${modulo}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Errore download template");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Seleziona un file CSV"); return; }
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/admin/ris/extraction/bulk-import?dry_run=${dryRun}&overwrite=${overwrite}`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(r.data);
      const s = r.data.summary;
      const msg = dryRun
        ? `Anteprima: ${s.imported}/${s.rows_total} importabili`
        : `Importate ${s.imported} celle (skipped ${s.skipped_invalid}, conflitti ${s.conflicts})`;
      toast.success(msg);
      if (!dryRun && onImported) onImported();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 413) {
        toast.error("File troppo grande (max 5 MB)");
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore import");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2.5 pt-2.5 border-t border-zinc-200">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">Import bulk CSV</div>
        <button
          type="button"
          onClick={downloadTemplate}
          className="text-[10px] text-blue-700 hover:underline"
          data-testid="ris-csv-template-btn"
        >
          ↓ Scarica template
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <select
          value={modulo}
          onChange={(e) => setModulo(e.target.value)}
          className="text-[10px] h-7 rounded border border-zinc-200 bg-white px-1.5"
          data-testid="ris-bulk-modulo"
        >
          <option value="extra_ricovero">Extra Ricovero</option>
          <option value="franchigie_rsm">Franchigie RSM</option>
        </select>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-[10px]"
          data-testid="ris-bulk-file"
        />
        <label className="flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="ris-bulk-dryrun" />
          Dry-run
        </label>
        <label className="flex items-center gap-1 cursor-pointer">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="ris-bulk-overwrite" />
          Sovrascrivi
        </label>
        <Button size="sm" onClick={submit} disabled={!file || busy} className="h-7 text-[10px]" data-testid="ris-bulk-submit">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
          Importa
        </Button>
      </div>

      {result && (
        <div className={`mt-2 p-2 rounded border text-[10px] ${result.dry_run ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="ris-bulk-result">
          {result.dry_run && (
            <div className="text-blue-900 font-medium mb-1">★ Anteprima — niente è stato salvato</div>
          )}
          <div className="grid grid-cols-3 gap-1">
            <div><strong>{result.summary.rows_total}</strong> righe</div>
            <div className="text-emerald-700">✓ {result.summary.imported} importate</div>
            <div className="text-amber-700">⚠ {result.summary.skipped_invalid} skip</div>
            <div className="text-purple-700">⇆ {result.summary.duplicates} duplicati</div>
            <div className="text-rose-700">⚡ {result.summary.conflicts} conflitti</div>
            <div className="text-zinc-700">↻ {result.summary.overwritten} overwrite</div>
          </div>
          {(result.errors?.length > 0 || result.conflicts?.length > 0) && (
            <button
              onClick={() => setShowErrors(!showErrors)}
              className="mt-1 text-blue-700 underline"
              data-testid="ris-bulk-toggle-errors"
            >
              {showErrors ? "Nascondi" : "Mostra"} dettagli ({(result.errors?.length || 0) + (result.conflicts?.length || 0)})
            </button>
          )}
          {showErrors && (
            <pre className="mt-1 bg-white/60 p-1 rounded text-[9px] overflow-x-auto max-h-32">
              {JSON.stringify({ errors: result.errors, conflicts: result.conflicts }, null, 1)}
            </pre>
          )}
          {!result.dry_run && result.backup_path && (
            <div className="mt-1 text-zinc-500 italic">Backup: {result.backup_path}</div>
          )}
        </div>
      )}
    </div>
  );
}
